import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Calculator } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function CajaChica() {
  const [cajas, setCajas] = useState([]);
  const [cajaActiva, setCajaActiva] = useState(null);
  const [movs, setMovs] = useState([]);
  const [arqueos, setArqueos] = useState([]);
  const [cuentasGasto, setCuentasGasto] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openMov, setOpenMov] = useState(false);
  const [openArqueo, setOpenArqueo] = useState(false);
  const [formCaja, setFormCaja] = useState({ nombre: "", responsable: "", monto_fijo: 100000 });
  const [formMov, setFormMov] = useState({ fecha: todayISO(), tipo: "gasto", monto: "", glosa: "", cuenta_gasto_id: "" });
  const [formArqueo, setFormArqueo] = useState({ fecha: todayISO(), saldo_fisico: "", observacion: "" });

  const loadCajas = async () => {
    try {
      const { data } = await api.get("/cajas-chicas");
      setCajas(data);
      if (!cajaActiva && data.length > 0) setCajaActiva(data[0]);
      else if (cajaActiva) {
        const upd = data.find((c) => c.id === cajaActiva.id);
        if (upd) setCajaActiva(upd);
      }
    } catch (e) { toast.error(extractError(e)); }
  };
  const loadMovs = async (cid) => {
    if (!cid) return;
    try {
      const [m, a] = await Promise.all([
        api.get(`/cajas-chicas/${cid}/movimientos`),
        api.get(`/cajas-chicas/${cid}/arqueos`),
      ]);
      setMovs(m.data); setArqueos(a.data);
    } catch (e) { toast.error(extractError(e)); }
  };
  const loadCuentas = async () => {
    try {
      const { data } = await api.get("/cuentas");
      setCuentasGasto(data.filter((c) => c.tipo === "egreso" && c.imputable));
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadCajas(); loadCuentas(); }, []);
  useEffect(() => { if (cajaActiva) loadMovs(cajaActiva.id); }, [cajaActiva]);

  const submitCaja = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cajas-chicas", { ...formCaja, monto_fijo: parseFloat(formCaja.monto_fijo) || 0 });
      toast.success("Caja chica creada");
      setOpenNew(false); setFormCaja({ nombre: "", responsable: "", monto_fijo: 100000 });
      loadCajas();
    } catch (err) { toast.error(extractError(err)); }
  };

  const submitMov = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cajas-chicas/movimientos", {
        caja_id: cajaActiva.id,
        fecha: formMov.fecha,
        tipo: formMov.tipo,
        monto: parseFloat(formMov.monto) || 0,
        glosa: formMov.glosa,
        cuenta_gasto_id: formMov.tipo === "gasto" ? formMov.cuenta_gasto_id || null : null,
      });
      toast.success(`${formMov.tipo === "gasto" ? "Gasto" : "Reposición"} registrado con asiento`);
      setOpenMov(false); setFormMov({ fecha: todayISO(), tipo: "gasto", monto: "", glosa: "", cuenta_gasto_id: "" });
      loadCajas(); loadMovs(cajaActiva.id);
    } catch (err) { toast.error(extractError(err)); }
  };

  const submitArqueo = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cajas-chicas/arqueos", {
        caja_id: cajaActiva.id,
        fecha: formArqueo.fecha,
        saldo_fisico: parseFloat(formArqueo.saldo_fisico) || 0,
        observacion: formArqueo.observacion,
      });
      const msg = data.estado === "cuadrado" ? "✓ Arqueo cuadrado" :
                  data.estado === "sobrante" ? `⚠ Sobrante ${fmtCLP(data.diferencia)}` :
                  `✗ Faltante ${fmtCLP(Math.abs(data.diferencia))}`;
      toast.success(msg);
      setOpenArqueo(false); setFormArqueo({ fecha: todayISO(), saldo_fisico: "", observacion: "" });
      loadMovs(cajaActiva.id);
    } catch (err) { toast.error(extractError(err)); }
  };

  const delMov = async (mid) => {
    if (!window.confirm("¿Eliminar movimiento y su asiento?")) return;
    try { await api.delete(`/cajas-chicas/movimientos/${mid}`); toast.success("Eliminado"); loadCajas(); loadMovs(cajaActiva.id); }
    catch (e) { toast.error(extractError(e)); }
  };

  const delCaja = async (cid, nom) => {
    if (!window.confirm(`¿Eliminar caja "${nom}"?`)) return;
    try { await api.delete(`/cajas-chicas/${cid}`); toast.success("Caja eliminada"); setCajaActiva(null); loadCajas(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <Page>
      <PageHeader
        title="Caja chica"
        subtitle="Gestión de cajas chicas con arqueos y reposiciones"
        testid="cc-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {cajaActiva && (
              <>
                <button onClick={() => setOpenMov(true)} data-testid="cc-new-mov"
                  className="inline-flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/10 text-sm px-3 py-1.5 rounded-sm">
                  <Plus className="h-4 w-4" /> Movimiento
                </button>
                <button onClick={() => setOpenArqueo(true)} data-testid="cc-arqueo"
                  className="inline-flex items-center gap-1.5 border border-warning-foreground text-warning-foreground hover:bg-warning/15 text-sm px-3 py-1.5 rounded-sm">
                  <Calculator className="h-4 w-4" /> Arqueo
                </button>
              </>
            )}
            <Dialog open={openNew} onOpenChange={setOpenNew}>
              <DialogTrigger asChild>
                <button data-testid="cc-new-caja"
                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-1.5 rounded-sm">
                  <Plus className="h-4 w-4" /> Nueva caja
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nueva caja chica</DialogTitle>
                  <DialogDescription>Define el monto fijo y responsable. El saldo se calculará desde gastos y reposiciones.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submitCaja} className="space-y-3">
                  <div>
                    <label className="label-caps block mb-1">Nombre</label>
                    <input required value={formCaja.nombre} onChange={(e) => setFormCaja({ ...formCaja, nombre: e.target.value })}
                      placeholder="Ej: Caja Oficina Principal"
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-caps block mb-1">Responsable</label>
                      <input value={formCaja.responsable} onChange={(e) => setFormCaja({ ...formCaja, responsable: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Monto fijo</label>
                      <input required type="number" min="1" value={formCaja.monto_fijo} onChange={(e) => setFormCaja({ ...formCaja, monto_fijo: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
                    </div>
                  </div>
                  <DialogFooter>
                    <button type="submit" className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">Crear</button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {cajas.length === 0 ? (
        <div className="bg-card border border-border rounded-md p-12 text-center text-sm text-muted-foreground">
          No tienes cajas chicas. Crea una con el botón superior.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {cajas.map((c) => (
              <div key={c.id} role="button" tabIndex={0}
                onClick={() => setCajaActiva(c)}
                onKeyDown={(e) => { if (e.key === "Enter") setCajaActiva(c); }}
                data-testid={`cc-card-${c.id}`}
                className={`text-left bg-card border rounded-md p-4 transition-all cursor-pointer ${cajaActiva?.id === c.id ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary/50"}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="label-caps">Caja</div>
                  <button onClick={(e) => { e.stopPropagation(); delCaja(c.id, c.nombre); }} className="text-destructive hover:bg-destructive/10 p-1 rounded-sm">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
                <div className="font-display text-lg font-medium mb-1">{c.nombre}</div>
                <div className="text-[12px] text-muted-foreground mb-2">{c.responsable || "Sin responsable"}</div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Saldo</span>
                  <span className={`num font-semibold ${c.saldo_actual < c.monto_fijo * 0.2 ? "text-destructive" : "text-primary"}`}>{fmtCLP(c.saldo_actual)}</span>
                </div>
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span>Fijo</span><span className="num">{fmtCLP(c.monto_fijo)}</span>
                </div>
              </div>
            ))}
          </div>

          {cajaActiva && (
            <>
              <h3 className="font-display text-lg mb-2 mt-4">Movimientos · {cajaActiva.nombre}</h3>
              <div className="bg-card border border-border rounded-md overflow-x-auto mb-6">
                {movs.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">Sin movimientos aún</div> : (
                  <table className="acc-table" data-testid="cc-mov-table">
                    <thead><tr><th>Fecha</th><th>Tipo</th><th>Glosa</th><th>Cuenta contra</th><th>Asiento</th><th className="num">Monto</th><th></th></tr></thead>
                    <tbody>
                      {movs.map((m) => (
                        <tr key={m.id}>
                          <td>{fmtDate(m.fecha)}</td>
                          <td><span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${m.tipo === "gasto" ? "bg-destructive/15 text-destructive" : "bg-success/15 text-primary"}`}>{m.tipo}</span></td>
                          <td className="text-[12px]">{m.glosa}</td>
                          <td className="text-[12px] text-muted-foreground">{m.cuenta_nombre}</td>
                          <td className="font-mono text-[11px]">#{m.asiento_numero}</td>
                          <td className={`num font-semibold ${m.tipo === "gasto" ? "text-destructive" : "text-primary"}`}>{fmtCLP(m.monto)}</td>
                          <td><button onClick={() => delMov(m.id)} className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"><Trash2 className="h-3.5 w-3.5" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <h3 className="font-display text-lg mb-2 mt-4">Historial de arqueos</h3>
              <div className="bg-card border border-border rounded-md overflow-x-auto">
                {arqueos.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">Sin arqueos aún</div> : (
                  <table className="acc-table" data-testid="cc-arqueos-table">
                    <thead><tr><th>Fecha</th><th className="num">Saldo libro</th><th className="num">Saldo físico</th><th className="num">Diferencia</th><th>Estado</th><th>Observación</th></tr></thead>
                    <tbody>
                      {arqueos.map((a) => (
                        <tr key={a.id}>
                          <td>{fmtDate(a.fecha)}</td>
                          <td className="num">{fmtCLP(a.saldo_libro)}</td>
                          <td className="num font-semibold">{fmtCLP(a.saldo_fisico)}</td>
                          <td className={`num font-semibold ${a.estado === "cuadrado" ? "text-primary" : "text-destructive"}`}>{fmtCLP(a.diferencia, { showSign: true })}</td>
                          <td><span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${a.estado === "cuadrado" ? "bg-success/15 text-primary" : "bg-destructive/15 text-destructive"}`}>{a.estado}</span></td>
                          <td className="text-[12px] text-muted-foreground">{a.observacion || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Modal Movimiento */}
      <Dialog open={openMov} onOpenChange={setOpenMov}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo movimiento · {cajaActiva?.nombre}</DialogTitle>
            <DialogDescription>Se generará un asiento contable automáticamente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMov} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Tipo</label>
                <Select value={formMov.tipo} onValueChange={(v) => setFormMov({ ...formMov, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gasto">Gasto (sale dinero)</SelectItem>
                    <SelectItem value="reposicion">Reposición (entra dinero)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-1">Fecha</label>
                <input required type="date" value={formMov.fecha} onChange={(e) => setFormMov({ ...formMov, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1">Monto</label>
              <input required type="number" min="1" value={formMov.monto} onChange={(e) => setFormMov({ ...formMov, monto: e.target.value })}
                data-testid="cc-mov-monto"
                className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
            </div>
            <div>
              <label className="label-caps block mb-1">Glosa</label>
              <input required value={formMov.glosa} onChange={(e) => setFormMov({ ...formMov, glosa: e.target.value })}
                data-testid="cc-mov-glosa"
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            {formMov.tipo === "gasto" && (
              <div>
                <label className="label-caps block mb-1">Cuenta de gasto</label>
                <Select value={formMov.cuenta_gasto_id} onValueChange={(v) => setFormMov({ ...formMov, cuenta_gasto_id: v })}>
                  <SelectTrigger><SelectValue placeholder="(Por defecto: 5.2.04 Gastos Generales)" /></SelectTrigger>
                  <SelectContent>
                    {cuentasGasto.map((c) => <SelectItem key={c.id} value={c.id}>{c.codigo} · {c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <button type="submit" data-testid="cc-mov-submit"
                className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">Registrar</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Arqueo */}
      <Dialog open={openArqueo} onOpenChange={setOpenArqueo}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arqueo de caja · {cajaActiva?.nombre}</DialogTitle>
            <DialogDescription>
              Saldo según libro: <strong className="font-mono">{fmtCLP(cajaActiva?.saldo_actual || 0)}</strong>. Cuenta el dinero físico y compara.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitArqueo} className="space-y-3">
            <div>
              <label className="label-caps block mb-1">Fecha</label>
              <input required type="date" value={formArqueo.fecha} onChange={(e) => setFormArqueo({ ...formArqueo, fecha: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="label-caps block mb-1">Saldo físico contado</label>
              <input required type="number" min="0" value={formArqueo.saldo_fisico} onChange={(e) => setFormArqueo({ ...formArqueo, saldo_fisico: e.target.value })}
                data-testid="cc-arqueo-fisico"
                className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
            </div>
            <div>
              <label className="label-caps block mb-1">Observación</label>
              <input value={formArqueo.observacion} onChange={(e) => setFormArqueo({ ...formArqueo, observacion: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <DialogFooter>
              <button type="submit" data-testid="cc-arqueo-submit"
                className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">Registrar arqueo</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
